import { FitGuidance, StartingGuide } from './fit-guidance-ui';
import { QuizRecovery } from './quiz-recovery';
import { PUBLIC_BROWSING } from './public-browsing';
import { StrictMode, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createRoot } from 'react-dom/client';
import {
  addAttachment, addInline, clearDraft, clearWrist, commit, confirmWrist, confirmWristPreference,
  createEmptySnapshot, createHistory, createReviewPreview, formatWristUm, invalidateWristConfirmation, loadDraft, MAX_ATTACHMENTS, MAX_GIFT_MESSAGE,
  MAX_INLINE_ITEMS, moveAttachmentAnchor, moveInline, parseWristToUm, redo, removeAttachment,
  removeInline, replaceSelected, resolveDraftLock, resolveServerDesignDiscovery, resolveStorage, saveDraft, type DraftLockManager,
  type DraftWriteResult, type FitPreference, type OperationResult, type PackagingOption, type ReviewPreview, type Snapshot,
  type StorageLike, type WristUnit, FORMAL_STORAGE_KEY, STORAGE_KEY, undo, withDraftLock,
} from './model';
import {
  ATTACHMENT_BY_ID, ATTACHMENT_FIXTURES, ATTACHMENT_IDS, configureCatalog, INLINE_BY_ID, INLINE_FIXTURES, INLINE_IDS,
  CATALOG_CONTEXT, PACKAGING_FIXTURES, THEME_STARTING_DESIGNS,
} from './fixtures';
import {
  getDesignerTheme, parseThemeSlug, replaceThemeInUrl, THEME_CHOICES, type ThemeSlug,
} from './themes';
import { QuizApp } from './quiz';
import { applyDemoTemplate } from './recommendation-model';
import { configureFormalRecommendation } from './formal-recommendation';
import './styles.css';
import { MobileDesigner } from './mobile-designer';

type InitialState = {
  snapshot: Snapshot;
  storageMode: StorageMode;
  notice: string | null;
  savedAt: string | null;
  storage: StorageLike | null;
  lockManager: DraftLockManager | null;
  revision: number | null;
  storageKey: string;
  lockName: string;
};
type StorageMode = 'ready' | 'invalid' | 'unavailable' | 'conflict' | 'paused';

function initialState(): InitialState {
  const formalPreview = CATALOG_CONTEXT.mode === 'formal_preview';
  const formalLive = formalPreview && readAccountContext().formal_commerce_enabled;
  const storageKey = formalPreview ? FORMAL_STORAGE_KEY : STORAGE_KEY;
  const lockName = formalPreview ? 'gbm-studio-formal-draft-v1' : 'gbm-studio-draft-v1';
  const storage = resolveStorage(() => window.localStorage);
  const lockManager = resolveDraftLock(() => navigator.locks as unknown as DraftLockManager);
  const result = loadDraft(storage, INLINE_IDS, ATTACHMENT_IDS, storageKey);
  if (result.status === 'invalid') return { snapshot: result.snapshot, storageMode: 'invalid', notice: result.message, savedAt: null, storage, lockManager, revision: null, storageKey, lockName };
  if (result.status === 'unavailable') return { snapshot: result.snapshot, storageMode: 'unavailable', notice: result.message, savedAt: null, storage, lockManager, revision: null, storageKey, lockName };
  if (!lockManager) return {
    snapshot: result.snapshot, storageMode: 'paused', savedAt: result.status === 'loaded' ? result.savedAt : null,
    notice: 'Reliable same-origin draft locking is unavailable. This draft can be edited in this tab, but automatic saving is paused.',
    storage, lockManager, revision: result.revision, storageKey, lockName,
  };
  if (result.status === 'loaded') return {
    snapshot: result.snapshot, storageMode: 'ready', notice: formalPreview
      ? formalLive
        ? 'Your private browser draft was restored.'
        : 'Your separate Formal preview draft was restored. It is never mixed with the TEST draft.'
      : 'Draft restored. Current approved images are shown; your components and order are unchanged.',
    savedAt: result.savedAt, storage, lockManager, revision: result.revision, storageKey, lockName,
  };
  return {
    snapshot: result.snapshot, storageMode: 'ready',
    notice: formalPreview
      ? formalLive
        ? 'This private browser draft stays on this device until you continue through the secure order path.'
        : 'Formal preview uses a separate browser-only draft. It is never mixed with the TEST draft.'
      : null,
    savedAt: null, storage, lockManager, revision: result.revision, storageKey, lockName,
  };
}

function formatTime(value: string | null): string {
  if (!value) return 'Not saved yet';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'Saved in this browser' : `Saved at ${parsed.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
}

function initialTheme(): ThemeSlug | null {
  const url = new URL(window.location.href);
  const raw = url.searchParams.get('intention');
  const theme = parseThemeSlug(raw);
  if (raw !== null && theme === null) {
    window.history.replaceState(window.history.state, '', replaceThemeInUrl(url.href, null));
  }
  return theme;
}

function BraceletCanvas({ snapshot, onSelect }: { snapshot: Snapshot; onSelect?: (uid: string) => void }) {
  const items = snapshot.design.inline_items;
  const points = useMemo(() => items.map((item, index) => {
    const angle = (Math.PI * 2 * index) / Math.max(items.length, 1) - Math.PI / 2;
    return { item, x: 180 + Math.cos(angle) * 115, y: 145 + Math.sin(angle) * 92 };
  }), [items]);
  const pointMap = new Map(points.map((point) => [point.item.uid, point]));

  return (
    <div className="gbm-designer-canvas-wrap">
      <svg className="gbm-designer-canvas" viewBox="0 0 360 300" role="img" aria-label={`Bracelet layout with ${items.length} inline pieces`}>
        <ellipse cx="180" cy="145" rx="115" ry="92" className="gbm-designer-guide" />
        {items.length === 0 && <text x="180" y="140" textAnchor="middle" className="gbm-designer-empty-title">Begin with one piece</text>}
        {items.length === 0 && <text x="180" y="164" textAnchor="middle" className="gbm-designer-empty-note">Explore Stones to start your design</text>}
        {points.map(({ item, x, y }, index) => {
          const fixture = INLINE_BY_ID.get(item.component_id);
          const selected = snapshot.selectedInlineUid === item.uid;
          const size = fixture?.size ?? 16;
          return (
            <g key={item.uid} className="gbm-designer-svg-piece" role={onSelect ? 'button' : undefined} tabIndex={onSelect ? 0 : undefined}
              aria-label={`${onSelect ? 'Select ' : ''}piece ${index + 1}: ${fixture?.name ?? 'Unknown piece'}`}
              onClick={onSelect ? () => onSelect(item.uid) : undefined}
              onKeyDown={onSelect ? (event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onSelect(item.uid); } } : undefined}>
              {onSelect && selected && <circle cx={x} cy={y} r={size + 3} className="gbm-designer-selection-ring" />}
              {fixture?.imageUrl ? <image href={fixture.imageUrl} x={x-size*512/480} y={y-size*512/480} width={size*2*512/480} height={size*2*512/480} preserveAspectRatio="xMidYMid meet" /> : <circle cx={x} cy={y} r={size} fill={CATALOG_CONTEXT.mode === 'test_fixture' ? fixture?.color ?? '#777' : 'none'} stroke="#b7aba0" className="gbm-designer-bead" />}
              {!fixture?.imageUrl && <circle cx={x - size * .28} cy={y - size * .3} r={Math.max(2, size * .22)} fill={fixture?.accent ?? '#aaa'} opacity="0.78" />}
            </g>
          );
        })}
        {snapshot.design.attachments.map((attachment) => {
          const anchor = pointMap.get(attachment.anchor_uid);
          if (!anchor) return null;
          const dx = anchor.x - 180;
          const dy = anchor.y - 145;
          const length = Math.max(Math.hypot(dx, dy), 1);
          const x = anchor.x + (dx / length) * 30;
          const y = anchor.y + (dy / length) * 30;
          return (
            <g key={attachment.uid}>
              <line x1={anchor.x} y1={anchor.y} x2={x} y2={y} className="gbm-designer-charm-line" />
              {ATTACHMENT_BY_ID.get(attachment.component_id)?.imageUrl ? <image href={ATTACHMENT_BY_ID.get(attachment.component_id)?.imageUrl} x={x-14} y={y-14} width="28" height="28" preserveAspectRatio="xMidYMid meet" /> : <path d={`M ${x} ${y - 9} L ${x + 3} ${y - 3} L ${x + 9} ${y - 2} L ${x + 4} ${y + 3} L ${x + 5} ${y + 9} L ${x} ${y + 6} L ${x - 5} ${y + 9} L ${x - 4} ${y + 3} L ${x - 9} ${y - 2} L ${x - 3} ${y - 3} Z`}
                fill={ATTACHMENT_BY_ID.get(attachment.component_id)?.color ?? '#b88a42'} className="gbm-designer-charm" />}
            </g>
          );
        })}
      </svg>
      <p className="gbm-designer-scale-note">Design preview. Natural patterns may vary. Relative sizes only; fit is not confirmed.</p>
    </div>
  );
}

type ReviewSession = { preview: ReviewPreview; themeName: string; themeSlug: ThemeSlug | null };

type CommerceQuote = {
  fit_guidance?: { confirmation_allowed: boolean; fit_status: string } | null;
  designer_media?: Record<string, { version?: string }>;
  quote_id: string; design_id: string; design_version: number; total_minor: number; unit_subtotal_minor: number;
  breakdown: { components_minor: number; assembly_minor: number; packaging_minor: number };
  expires_at: string; availability: { status: string };
};
type CommerceSnapshot = { snapshot_id: string; quote_id: string; design_id: string; design_version: number; total_minor: number };
type CommerceOrder = {
  order_id: number; order_number: string; reservation_id: string; reservation_status: string;
  item_total_minor: number; shipping_minor: number; total_minor: number; shipping_rate_id: string; shipping_label: string;
};
type ServerDesign = {
  design_id: string; design_version: number; design_hash: string; design: Snapshot['design'];
  wrist_state: Snapshot['wristState']; gift_message: string; saved_at: string;
  recovery_scope: 'same_test_session_only' | 'account_private'; claimed_to_account?: boolean;
};
type LatestDesign = { design_id: string; design_version: number; updated_at: string };
type OwnerScope = 'session' | 'account';
type AccountContext = {
  logged_in: boolean; rest_nonce: string; account_url: string; catalog_mode: 'test' | 'formal'; can_catalog_preview: boolean; can_manage_studio: boolean;
  formal_commerce_enabled: boolean; formal_verification: boolean;
};
type ShippingDestination = { country: string; state: string; postcode: string; city: string };
type ShippingAddress = ShippingDestination & {
  first_name: string; last_name: string; address_1: string; address_2: string; email: string; phone: string;
};
type ShippingRate = { rate_id: string; label: string; method_id: string; instance_id: number; shipping_minor: number };
type ShippingQuote = {
  destination: ShippingDestination; rates: ShippingRate[]; selected_rate: ShippingRate;
  item_total_minor: number; shipping_minor: number; total_minor: number; currency: 'USD'; policy: 'TEST_UNAPPROVED' | 'CONFIGURED_WOO_ZONE';
};
type ShippingConfig = { countries: Record<string, string>; states: Record<string, Record<string, string>>; currency: 'USD' };

function readAccountContext(): AccountContext {
  const fallback: AccountContext = {
    logged_in: false, rest_nonce: '', account_url: '/my-account/', catalog_mode: 'test', can_catalog_preview: false, can_manage_studio: false,
    formal_commerce_enabled: false, formal_verification: false,
  };
  const raw = document.getElementById('gbm-studio-demo')?.dataset.gbmAccount;
  if (!raw) return fallback;
  try {
    const parsed = JSON.parse(raw) as Partial<AccountContext>;
    return {
      logged_in: parsed.logged_in === true,
      rest_nonce: typeof parsed.rest_nonce === 'string' ? parsed.rest_nonce : '',
      account_url: typeof parsed.account_url === 'string' ? parsed.account_url : fallback.account_url,
      catalog_mode: parsed.catalog_mode === 'formal' ? 'formal' : 'test',
      can_catalog_preview: parsed.can_catalog_preview === true, can_manage_studio: parsed.can_manage_studio === true,
      formal_commerce_enabled: parsed.formal_commerce_enabled === true,
      formal_verification: parsed.formal_verification === true,
    };
  } catch { return fallback; }
}

function readShippingConfig(): ShippingConfig {
  const fallback: ShippingConfig = { countries: { US: 'United States (US)' }, states: { US: { CA: 'California' } }, currency: 'USD' };
  const raw = document.getElementById('gbm-studio-demo')?.dataset.gbmShipping;
  if (!raw) return fallback;
  try {
    const parsed = JSON.parse(raw) as Partial<ShippingConfig>;
    return parsed.countries && parsed.states
      ? { countries: parsed.countries, states: parsed.states, currency: 'USD' }
      : fallback;
  } catch { return fallback; }
}

function money(minor: number): string { return `$${(minor / 100).toFixed(2)}`; }
function commerceKey(prefix: string): string { return `${prefix}-${crypto.randomUUID().replaceAll('-', '')}`; }

function serverSnapshot(saved: ServerDesign): Snapshot {
  const uids = [...saved.design.inline_items, ...saved.design.attachments].map((item) => item.uid);
  let highest = 0;
  for (const uid of uids) {
    const match = /^(?:bead|charm)-([0-9a-z]+)$/.exec(uid);
    if (!match) continue;
    const value = Number.parseInt(match[1], 36);
    if (Number.isSafeInteger(value)) highest = Math.max(highest, value);
  }
  return {
    design: saved.design, wristState: saved.wrist_state, giftMessage: saved.gift_message,
    selectedInlineUid: saved.design.inline_items[0]?.uid ?? null,
    nextUid: Number.isSafeInteger(highest + 1) && highest + 1 > 0 ? highest + 1 : 1,
  };
}

function ServerDesignControls({ snapshot, designId, designVersion, ownerScope, onIdentity, onRestore, onAccountProtected }: {
  snapshot: Snapshot; designId: string | null; designVersion: number; ownerScope: OwnerScope;
  onIdentity: (id: string, version: number, scope: OwnerScope) => void;
  onRestore: (snapshot: Snapshot, scope: OwnerScope) => void; onAccountProtected: () => void;
}) {
  const account = useMemo(readAccountContext, []);
  const [csrf, setCsrf] = useState<string | null>(null);
  const [available, setAvailable] = useState<ServerDesign | null>(null);
  const [availableScope, setAvailableScope] = useState<OwnerScope>('session');
  const [separateSave, setSeparateSave] = useState(false);
  const [busy, setBusy] = useState<string | null>('Opening your private design session…');
  const [message, setMessage] = useState<string | null>(null);
  const alive = useRef(true);

  async function request<T>(path: string, init: RequestInit, scope: OwnerScope = 'session'): Promise<T> {
    const headers = new Headers(init.headers);
    if (account.logged_in) headers.set('X-WP-Nonce', account.rest_nonce);
    if (scope === 'account') {
      headers.set('X-GBM-Owner-Scope', 'account');
      headers.set('X-WP-Nonce', account.rest_nonce);
    }
    const response = await fetch(`/wp-json/gbm-studio/v1/test-commerce/${path}`, { credentials: 'same-origin', ...init, headers });
    const result = await response.json() as T & { message?: string; code?: string };
    if (!response.ok) throw new Error(result.message ?? result.code ?? 'The saved design request failed.');
    return result;
  }

  async function load(id: string, token: string, scope: OwnerScope): Promise<ServerDesign> {
    return request<ServerDesign>(`designs/${encodeURIComponent(id)}`, { headers: { 'X-GBM-CSRF': token } }, scope);
  }

  useEffect(() => {
    alive.current = true;
    void (async () => {
      try {
        const bootstrap = await request<{ csrf_token: string; latest_design: LatestDesign | null }>(
          'bootstrap', { headers: account.logged_in ? { 'X-WP-Nonce': account.rest_nonce } : {} },
        );
        if (!alive.current) return;
        setCsrf(bootstrap.csrf_token);
        const requestedAccountDesign = new URLSearchParams(window.location.search).get('account_design');
        if (account.logged_in && requestedAccountDesign && /^GBM-D-[A-F0-9]{12}$/.test(requestedAccountDesign)) {
          const saved = await load(requestedAccountDesign, bootstrap.csrf_token, 'account');
          if (!alive.current) return;
          setAvailable(saved); setAvailableScope('account'); setSeparateSave(false);
          onIdentity(saved.design_id, saved.design_version, 'account'); onRestore(serverSnapshot(saved), 'account');
          setMessage(`Restored ${saved.design_id}, version ${saved.design_version} from your private account library.`);
          return;
        }
        if (bootstrap.latest_design) {
          const saved = await load(bootstrap.latest_design.design_id, bootstrap.csrf_token, 'session');
          if (!alive.current) return;
          setAvailable(saved); setAvailableScope('session');
          const discovery = resolveServerDesignDiscovery(snapshot, { designId, designVersion }, saved);
          setSeparateSave(!designId && !discovery.adopted);
          if (discovery.adopted && discovery.identity.designId) {
            onIdentity(discovery.identity.designId, discovery.identity.designVersion, 'session');
            setMessage(`This page exactly matches ${saved.design_id}, version ${saved.design_version}.`);
          } else if (designId) {
            setMessage(`A different or newer private server version is available. This page remains based on ${designId}, version ${designVersion}; restore before replacing it.`);
          } else {
            setMessage('A private server-saved design is available for this browser session. Restore it to replace this page, or save this page as a separate design.');
          }
        }
      } catch (error) {
        if (alive.current) setMessage(error instanceof Error ? error.message : 'The saved design session is unavailable.');
      } finally { if (alive.current) setBusy(null); }
    })();
    return () => { alive.current = false; };
  }, []);

  function save() {
    if (!csrf) return;
    setBusy('Saving this design privately…'); setMessage(null);
    const body: Record<string, unknown> = {
      expected_version: designVersion, design: snapshot.design, wrist_state: snapshot.wristState,
      gift_message: snapshot.giftMessage,
    };
    if (designId) body.design_id = designId;
    void request<ServerDesign>('designs', {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'X-GBM-CSRF': csrf }, body: JSON.stringify(body),
    }, ownerScope).then((saved) => {
      if (!alive.current) return;
      setAvailable(saved); setAvailableScope(ownerScope); setSeparateSave(false); onIdentity(saved.design_id, saved.design_version, ownerScope);
      setMessage(ownerScope === 'account'
        ? `Saved to your private account as ${saved.design_id}, version ${saved.design_version}.`
        : `Saved for this private browser session as ${saved.design_id}, version ${saved.design_version}.`);
    }).catch((error: unknown) => {
      if (alive.current) setMessage(error instanceof Error ? error.message : 'The design was not saved.');
    }).finally(() => { if (alive.current) setBusy(null); });
  }

  function saveToAccount() {
    if (!csrf || !account.logged_in) return;
    setBusy('Saving this design to your account…'); setMessage(null);
    const body: Record<string, unknown> = {
      expected_version: designVersion, design: snapshot.design, wrist_state: snapshot.wristState,
      gift_message: snapshot.giftMessage,
    };
    if (designId) body.design_id = designId;
    void (async () => {
      let saved: ServerDesign;
      if (ownerScope === 'session' && designId) {
        const current = await request<ServerDesign>('designs', {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'X-GBM-CSRF': csrf }, body: JSON.stringify(body),
        }, 'session');
        saved = await request<ServerDesign>('account/designs/claim', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-GBM-CSRF': csrf, 'X-WP-Nonce': account.rest_nonce },
          body: JSON.stringify({ design_id: current.design_id }),
        }, 'account');
      } else {
        const accountBody = { ...body };
        if (ownerScope !== 'account') delete accountBody.design_id;
        if (ownerScope !== 'account') accountBody.expected_version = 0;
        saved = await request<ServerDesign>('designs', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(accountBody),
        }, 'account');
      }
      if (!alive.current) return;
      setAvailable(saved); setAvailableScope('account'); setSeparateSave(false);
      onIdentity(saved.design_id, saved.design_version, 'account');
      onAccountProtected();
      setMessage(`Saved to your private account as ${saved.design_id}, version ${saved.design_version}.`);
    })().catch((error: unknown) => {
      if (alive.current) setMessage(error instanceof Error ? error.message : 'The design was not saved to your account.');
    }).finally(() => { if (alive.current) setBusy(null); });
  }

  function restore() {
    if (!csrf || !available) return;
    setBusy('Loading the latest private server design…'); setMessage(null);
    void load(available.design_id, csrf, availableScope).then((saved) => {
      if (!alive.current) return;
      setAvailable(saved); setSeparateSave(false); onIdentity(saved.design_id, saved.design_version, availableScope); onRestore(serverSnapshot(saved), availableScope);
      setMessage(`Restored ${saved.design_id}, version ${saved.design_version}. Unsaved page edits were not merged.`);
    }).catch((error: unknown) => {
      if (alive.current) setMessage(error instanceof Error ? error.message : 'The saved design could not be restored.');
    }).finally(() => { if (alive.current) setBusy(null); });
  }

  return <section className="gbm-server-design" aria-labelledby="gbm-server-design-title">
    <div><p className="gbm-designer-eyebrow">Choose where this design lives</p><h2 id="gbm-server-design-title">Keep your design private</h2>
      <p>A browser draft stays on this device. A session save stays with this private browser session. An account save can be restored after you sign in again.</p></div>
    <div className="gbm-server-design__actions">
      <button type="button" disabled={!csrf || busy !== null} onClick={save}>{ownerScope === 'account' ? 'Save account design' : separateSave ? 'Save as a separate session design' : 'Save for this session'}</button>
      {account.logged_in && ownerScope !== 'account' && <button type="button" disabled={!csrf || busy !== null} onClick={saveToAccount}>Save to my account</button>}
      {available && <button type="button" className="is-quiet" disabled={busy !== null} onClick={restore}>Restore latest saved version</button>}
      {account.logged_in && <a className="gbm-server-design__link" href={account.account_url}>Open my saved designs</a>}
    </div>
    {busy && <p role="status">{busy}</p>}
    {message && <p role="status">{message}</p>}
  </section>;
}

function TestCommercePanel({ preview, designId, designVersion, ownerScope, onDesignSaved }: {
  preview: ReviewPreview; designId: string | null; designVersion: number; ownerScope: OwnerScope;
  onDesignSaved: (id: string, version: number, scope: OwnerScope) => void;
}) {
  const account = useMemo(readAccountContext, []);
  const shippingConfig = useMemo(readShippingConfig, []);
  const [csrf, setCsrf] = useState<string | null>(null);
  const [quote, setQuote] = useState<CommerceQuote | null>(null);
  const [confirmed, setConfirmed] = useState<CommerceSnapshot | null>(null);
  const [order, setOrder] = useState<CommerceOrder | null>(null);
  const [cartUrl, setCartUrl] = useState<string | null>(null);
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress>({
    first_name: '', last_name: '', address_1: '', address_2: '', email: '', phone: '',
    country: 'US', state: 'CA', postcode: '', city: '',
  });
  const [accountShippingAddress, setAccountShippingAddress] = useState<ShippingAddress | null>(null);
  const [shippingQuote, setShippingQuote] = useState<ShippingQuote | null>(null);
  const [shippingRateId, setShippingRateId] = useState<string>('');
  const [busy, setBusy] = useState<string | null>('Starting secure TEST session…');
  const [message, setMessage] = useState<string | null>(null);
  const aliveRef = useRef(true);
  const requestSequenceRef = useRef(0);
  const keysRef = useRef({
    quote: commerceKey('quote'), snapshot: commerceKey('snapshot'), cart: commerceKey('cart'),
    checkout: commerceKey('checkout'), success: commerceKey('success'), pick: commerceKey('pick'), cancel: commerceKey('cancel'), fail: commerceKey('fail'),
  });

  useEffect(() => {
    aliveRef.current = true;
    void (async () => {
      try {
        const response = await fetch('/wp-json/gbm-studio/v1/test-commerce/bootstrap', {
          credentials: 'same-origin', headers: account.logged_in ? { 'X-WP-Nonce': account.rest_nonce } : {},
        });
        const body = await response.json() as { csrf_token?: string; message?: string; account_shipping_address?: ShippingAddress | null };
        if (!response.ok || !body.csrf_token) throw new Error(body.message ?? 'Your design session could not be started.');
        if (aliveRef.current) { setCsrf(body.csrf_token); setAccountShippingAddress(body.account_shipping_address ?? null); setBusy(null); }
      } catch (error) {
        if (aliveRef.current) { setBusy(null); setMessage(error instanceof Error ? error.message : 'Your design session could not be started.'); }
      }
    })();
    return () => { aliveRef.current = false; };
  }, []);

  async function post<T>(path: string, body: Record<string, unknown>): Promise<T> {
    if (!csrf) throw new Error('The secure TEST session is not ready.');
    const headers = new Headers({ 'Content-Type': 'application/json', 'X-GBM-CSRF': csrf });
    // Preserve the signed-in Woo session even when the design stays session-owned.
    if (account.logged_in) headers.set('X-WP-Nonce', account.rest_nonce);
    if (ownerScope === 'account') {
      headers.set('X-GBM-Owner-Scope', 'account');
      headers.set('X-WP-Nonce', account.rest_nonce);
    }
    const response = await fetch(`/wp-json/gbm-studio/v1/test-commerce/${path}`, {
      method: 'POST', credentials: 'same-origin', headers, body: JSON.stringify(body),
    });
    const result = await response.json() as T & { message?: string; code?: string };
    if (!response.ok) throw new Error(result.message ?? result.code ?? 'The TEST commerce request failed.');
    return result;
  }

  async function run(label: string, operation: () => Promise<void>) {
    const sequence = ++requestSequenceRef.current;
    setBusy(label); setMessage(null);
    try { await operation(); }
    catch (error) { if (aliveRef.current && sequence === requestSequenceRef.current) setMessage(error instanceof Error ? error.message : 'The TEST commerce request failed.'); }
    finally { if (aliveRef.current && sequence === requestSequenceRef.current) setBusy(null); }
  }

  function requestQuote() {
    void run('Calculating the server quote…', async () => {
      const saveBody: Record<string, unknown> = {
        expected_version: designVersion, design: preview.snapshot.design, wrist_state: preview.snapshot.wristState,
        gift_message: preview.snapshot.giftMessage,
      };
      if (designId) saveBody.design_id = designId;
      const saved = await post<ServerDesign>('designs', saveBody);
      if (!aliveRef.current) return;
      onDesignSaved(saved.design_id, saved.design_version, ownerScope);
      const body: Record<string, unknown> = {
        design: preview.snapshot.design, wrist_state: preview.snapshot.wristState, gift_message: preview.snapshot.giftMessage,
        quantity: 1, idempotency_key: keysRef.current.quote, design_id: saved.design_id,
      };
      const result = await post<CommerceQuote>('quotes', body);
      if (!aliveRef.current) return;
      if (result.designer_media && Object.entries(result.designer_media).some(([sku, media]) => (media.version ?? '') !== (INLINE_BY_ID.get(sku)?.mediaVersion ?? ATTACHMENT_BY_ID.get(sku)?.mediaVersion ?? ''))) {
        setQuote(null); setConfirmed(null); throw new Error('Component images changed. Your draft is safe. Refresh this page to review the updated images before quoting again.');
      }
      setQuote(result); setConfirmed(null); setOrder(null); setCartUrl(null); setShippingQuote(null); setShippingRateId(''); onDesignSaved(result.design_id, result.design_version, ownerScope);
      setMessage(result.availability.status === 'sufficient' ? 'Server quote is valid. Inventory check is read-only until checkout.' : 'The quote is calculated, but exact inventory is short.');
    });
  }

  function confirmSnapshot() {
    if (!quote) return;
    void run('Confirming an immutable design snapshot…', async () => {
      const result = await post<CommerceSnapshot>('snapshots', { quote_id: quote.quote_id, idempotency_key: keysRef.current.snapshot });
      if (!aliveRef.current) return;
      setConfirmed(result); setShippingQuote(null); setShippingRateId(''); setMessage('Snapshot confirmed. Further edits will create a new design version; this snapshot will not change.');
    });
  }

  function addCart() {
    if (!confirmed) return;
    void run('Adding the validated snapshot to Woo cart…', async () => {
      const result = await post<{ cart_url: string }>('cart', { snapshot_id: confirmed.snapshot_id, idempotency_key: keysRef.current.cart });
      if (!aliveRef.current) return;
      setCartUrl(result.cart_url); setShippingQuote(null); setShippingRateId(''); setMessage('Woo cart now references this exact snapshot. Inventory is still not reserved. Calculate TEST delivery before creating the order.');
    });
  }

  function shippingDestination(address: ShippingAddress): ShippingDestination {
    return { country: address.country, state: address.state, postcode: address.postcode, city: address.city };
  }

  function updateShippingAddress(field: keyof ShippingAddress, value: string) {
    setShippingAddress((current) => {
      if (field !== 'country') return { ...current, [field]: value };
      const states = shippingConfig.states[value] ?? {};
      const preferred = value === 'US' && states.CA ? 'CA' : value === 'CA' && states.ON ? 'ON' : Object.keys(states)[0] ?? '';
      return { ...current, country: value, state: preferred, postcode: '', city: '' };
    });
    if (field === 'country' || field === 'state' || field === 'postcode' || field === 'city') {
      setShippingQuote(null); setShippingRateId('');
    }
    setOrder(null);
  }

  function useAccountShippingAddress() {
    if (!accountShippingAddress) return;
    setShippingAddress(accountShippingAddress); setShippingQuote(null); setShippingRateId(''); setOrder(null);
    setMessage('Your saved Woo address was copied into this form for this TEST order only. It was not changed in your account.');
  }

  function requestShipping() {
    if (!confirmed || !cartUrl) return;
    void run('Calculating TEST delivery from Woo shipping zones…', async () => {
      const result = await post<ShippingQuote>('shipping', { snapshot_id: confirmed.snapshot_id, shipping_destination: shippingDestination(shippingAddress) });
      if (!aliveRef.current) return;
      setShippingAddress((current) => ({ ...current, ...result.destination })); setShippingQuote(result); setShippingRateId(result.selected_rate.rate_id);
      setMessage(`${result.selected_rate.label}: ${money(result.shipping_minor)} ${result.currency}. Final rates, timing, military-address support, and cross-border charges still need operating approval.`);
    });
  }

  function checkout() {
    if (!confirmed || !cartUrl || !shippingQuote || !shippingRateId) return;
    void run('Creating the no-charge TEST order and reserving exact components…', async () => {
      const result = await post<CommerceOrder>('checkout', {
        snapshot_id: confirmed.snapshot_id, idempotency_key: keysRef.current.checkout,
        shipping_destination: shippingDestination(shippingAddress), shipping_rate_id: shippingRateId, shipping_address: shippingAddress,
      });
      if (!aliveRef.current) return;
      setOrder(result); setMessage(`Pending TEST order created at ${money(result.total_minor)} USD, including ${money(result.shipping_minor)} TEST delivery. Exact component quantities are reserved for 30 minutes.`);
    });
  }

  function transition(action: 'success' | 'pick' | 'cancel' | 'fail') {
    if (!order) return;
    const key = keysRef.current[action];
    void run(`Applying controlled TEST action: ${action}…`, async () => {
      const result = await post<{ reservation_status: string; message?: string }>(`orders/${order.order_id}/transition`, {
        action, idempotency_key: key,
      });
      if (!aliveRef.current) return;
      setOrder((current) => current ? { ...current, reservation_status: result.reservation_status } : current);
      setMessage(result.message ?? `TEST action complete. Inventory state: ${result.reservation_status}.`);
    });
  }

  const wristReady = preview.wrist.status === 'confirmed' && preview.snapshot.wristState?.confirmed === true;
  const designReady = wristReady && preview.snapshot.design.packaging_option_id !== null;
  const chosenShippingRate = shippingQuote?.rates.find((rate) => rate.rate_id === shippingRateId) ?? shippingQuote?.selected_rate ?? null;
  const chosenShippingMinor = chosenShippingRate?.shipping_minor ?? 0;
  return <section className="gbm-commerce" aria-labelledby="gbm-commerce-title">
    <div className="gbm-commerce__heading">
      <div><p className="gbm-designer-eyebrow">Staging transaction path</p><h2 id="gbm-commerce-title">Test Checkout – No Real Charge</h2></div>
      <span>TEST ONLY</span>
    </div>
    <p>This controlled route uses a real server quote, immutable snapshot, Woo cart/order, Woo shipping zones, and exact batch reservation. The confirmed first-market scope is the United States, separately coded U.S. territories, and Canada. Rates are unapproved USD fixtures; no carrier, payment provider, shipment, or customer email is contacted.</p>
    {!designReady && <p className="gbm-commerce__warning">Confirm the wrist input and choose packaging before requesting a server quote.</p>}
    {busy && <p className="gbm-commerce__status" role="status">{busy}</p>}
    {message && <p className="gbm-commerce__status" role="status">{message}</p>}
    <div className="gbm-commerce__steps">
      <article><span>1</span><h3>Server quote</h3><button type="button" disabled={!csrf || !designReady || busy !== null} onClick={requestQuote}>Request TEST quote</button>
        {quote && <dl><div><dt>Components</dt><dd>{money(quote.breakdown.components_minor)}</dd></div><div><dt>Making fee</dt><dd>{money(quote.breakdown.assembly_minor)}</dd></div><div><dt>Packaging</dt><dd>{money(quote.breakdown.packaging_minor)}</dd></div><div><dt>Total</dt><dd>{money(quote.total_minor)}</dd></div></dl>}
      </article>
      <article><span>2</span><h3>Immutable snapshot</h3><button type="button" disabled={!quote || quote.fit_guidance?.confirmation_allowed === false || busy !== null} onClick={confirmSnapshot}>Confirm design</button>
        {confirmed && <small>{confirmed.snapshot_id}<br />Design v{confirmed.design_version} · {confirmed.quote_id}</small>}
      </article>
      <article><span>3</span><h3>Woo cart</h3><button type="button" disabled={!confirmed || busy !== null} onClick={addCart}>Add confirmed snapshot</button>
        {cartUrl && <a href={cartUrl}>Open Woo cart</a>}
      </article>
      <article className="gbm-commerce__delivery"><span>4</span><h3>Delivery &amp; checkout</h3>
        <form className="gbm-commerce__checkout-form" onSubmit={(event) => { event.preventDefault(); checkout(); }}>
        {accountShippingAddress && <button type="button" className="is-quiet" onClick={useAccountShippingAddress}>Use my saved Woo address</button>}
        <div className="gbm-commerce__address">
          <label>First name<input required autoComplete="shipping given-name" value={shippingAddress.first_name} maxLength={60} onChange={(event) => updateShippingAddress('first_name', event.target.value)} /></label>
          <label>Last name<input required autoComplete="shipping family-name" value={shippingAddress.last_name} maxLength={60} onChange={(event) => updateShippingAddress('last_name', event.target.value)} /></label>
          <label className="is-wide">Street address<input required autoComplete="shipping address-line1" value={shippingAddress.address_1} maxLength={100} onChange={(event) => updateShippingAddress('address_1', event.target.value)} /></label>
          <label className="is-wide">Apartment, suite, etc. <span>(optional)</span><input autoComplete="shipping address-line2" value={shippingAddress.address_2} maxLength={100} onChange={(event) => updateShippingAddress('address_2', event.target.value)} /></label>
          <label>Country or territory<select required autoComplete="shipping country" value={shippingAddress.country} onChange={(event) => updateShippingAddress('country', event.target.value)}>
            {Object.entries(shippingConfig.countries).map(([code, label]) => <option key={code} value={code}>{label}</option>)}
          </select></label>
          {(Object.keys(shippingConfig.states[shippingAddress.country] ?? {}).length > 0) && <label>State or province<select required autoComplete="shipping address-level1" value={shippingAddress.state} onChange={(event) => updateShippingAddress('state', event.target.value)}>
            {Object.entries(shippingConfig.states[shippingAddress.country] ?? {}).map(([code, label]) => <option key={code} value={code}>{label}</option>)}
          </select></label>}
          <label>City<input required autoComplete="shipping address-level2" value={shippingAddress.city} maxLength={80} onChange={(event) => updateShippingAddress('city', event.target.value)} /></label>
          <label>ZIP or postal code<input required autoComplete="shipping postal-code" value={shippingAddress.postcode} maxLength={20}
            pattern={shippingAddress.country === 'CA' ? '[A-Za-z][0-9][A-Za-z][ -]?[0-9][A-Za-z][0-9]' : '[0-9]{5}(-[0-9]{4})?'}
            title={shippingAddress.country === 'CA' ? 'Use a Canadian postal code such as M5V 2T6.' : 'Use a 5-digit ZIP code, optionally followed by 4 digits.'}
            onChange={(event) => updateShippingAddress('postcode', event.target.value)} /></label>
          <label className="is-wide">Contact email<input required type="email" autoComplete="email" value={shippingAddress.email} maxLength={254} onChange={(event) => updateShippingAddress('email', event.target.value)} /></label>
          <label className="is-wide">Phone <span>(optional)</span><input type="tel" autoComplete="tel" value={shippingAddress.phone} maxLength={40} onChange={(event) => updateShippingAddress('phone', event.target.value)} /></label>
        </div>
        <p className="gbm-commerce__privacy">Used only in Woo’s private TEST order fields. This form does not save over your account address, design snapshot, or shared browser draft. Format checks do not confirm carrier deliverability.</p>
        <button type="button" className="is-quiet" disabled={!confirmed || !cartUrl || busy !== null} onClick={requestShipping}>Calculate TEST delivery</button>
        {shippingQuote && <div className="gbm-commerce__rates">{shippingQuote.rates.map((rate) => <label key={rate.rate_id}>
          <input type="radio" name="gbm-shipping-rate" value={rate.rate_id} checked={shippingRateId === rate.rate_id} onChange={() => setShippingRateId(rate.rate_id)} />
          <span>{rate.label}</span><strong>{money(rate.shipping_minor)} USD</strong>
        </label>)}<dl><div><dt>Design</dt><dd>{money(shippingQuote.item_total_minor)}</dd></div><div><dt>Delivery</dt><dd>{money(chosenShippingMinor)}</dd></div><div><dt>TEST total</dt><dd>{money(shippingQuote.item_total_minor + chosenShippingMinor)} USD</dd></div></dl></div>}
        <button type="submit" disabled={!confirmed || !cartUrl || !shippingQuote || !shippingRateId || busy !== null}>Create no-charge TEST order</button>
        {order && <small>Order #{order.order_number}<br />{order.shipping_label} · {money(order.shipping_minor)} USD<br />Total {money(order.total_minor)} USD · {order.reservation_status}</small>}
        </form>
      </article>
    </div>
    {order && <div className="gbm-commerce__actions" aria-label="Controlled TEST order actions">
      <button type="button" disabled={busy !== null || order.reservation_status !== 'reserved'} onClick={() => transition('success')}>Simulate payment success</button>
      <button type="button" disabled={busy !== null || order.reservation_status !== 'allocated'} onClick={() => transition('pick')}>Simulate workshop pick</button>
      <button type="button" className="is-quiet" disabled={busy !== null || order.reservation_status === 'released'} onClick={() => transition('cancel')}>Cancel &amp; release unpicked</button>
      <button type="button" className="is-quiet" disabled={busy !== null || order.reservation_status !== 'reserved'} onClick={() => transition('fail')}>Simulate failure</button>
    </div>}
  </section>;
}

function preferenceLabel(value: FitPreference | null): string {
  if (value === 'snug') return 'Snug';
  if (value === 'comfort') return 'Comfort';
  if (value === 'loose') return 'Loose';
  return 'Not selected';
}

function FormalCatalogPreviewPanel({ preview }: { preview: ReviewPreview }) {
  const account = useMemo(readAccountContext, []);
  const shippingConfig = useMemo(readShippingConfig, []);
  const [csrf, setCsrf] = useState<string | null>(null);
  const [quote, setQuote] = useState<CommerceQuote | null>(null);
  const [confirmed, setConfirmed] = useState<CommerceSnapshot | null>(null);
  const [shippingQuote, setShippingQuote] = useState<ShippingQuote | null>(null);
  const [shippingRateId, setShippingRateId] = useState('');
  const [order, setOrder] = useState<(CommerceOrder & { payment_available: boolean; order_pay_url: string; payment_message: string }) | null>(null);
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress>({
    first_name: '', last_name: '', address_1: '', address_2: '', email: '', phone: '', country: 'US', state: 'CA', postcode: '', city: '',
  });
  const [busy, setBusy] = useState<string | null>('Opening the controlled formal checkout session…');
  const [message, setMessage] = useState<string | null>(null);
  const keys = useRef({ quote: commerceKey('formal-quote'), snapshot: commerceKey('formal-snapshot'), checkout: commerceKey('formal-checkout') });
  const canUse = account.catalog_mode === 'formal' && (account.formal_commerce_enabled || account.formal_verification);
  const designReady = preview.wrist.status === 'confirmed' && preview.snapshot.wristState?.confirmed === true
    && preview.snapshot.design.inline_items.length > 0 && Boolean(preview.snapshot.design.packaging_option_id);

  useEffect(() => {
    if (!canUse) { setBusy(null); setMessage('Formal ordering is currently closed.'); return; }
    let alive = true;
    void fetch('/wp-json/gbm-studio/v1/formal-commerce/bootstrap', {
      credentials: 'same-origin', headers: account.rest_nonce ? { 'X-WP-Nonce': account.rest_nonce } : {},
    }).then(async (response) => {
      const body = await response.json() as { csrf_token?: string; message?: string; account_shipping_address?: ShippingAddress | null };
      if (!response.ok || !body.csrf_token) throw new Error(body.message ?? 'The formal checkout session could not be opened.');
      if (!alive) return;
      setCsrf(body.csrf_token); if (body.account_shipping_address) setShippingAddress(body.account_shipping_address); setBusy(null);
    }).catch((error) => { if (alive) { setBusy(null); setMessage(error instanceof Error ? error.message : 'The formal checkout session could not be opened.'); } });
    return () => { alive = false; };
  }, [canUse]);

  async function post<T>(path: string, body: Record<string, unknown>): Promise<T> {
    if (!csrf) throw new Error('The formal checkout session is not ready.');
    const headers = new Headers({ 'Content-Type': 'application/json', 'X-GBM-CSRF': csrf });
    if (account.rest_nonce) headers.set('X-WP-Nonce', account.rest_nonce);
    const response = await fetch(`/wp-json/gbm-studio/v1/formal-commerce/${path}`, {
      method: 'POST', credentials: 'same-origin', headers, body: JSON.stringify(body),
    });
    const result = await response.json() as T & { message?: string; code?: string };
    if (!response.ok) throw new Error(result.message ?? result.code ?? 'The formal checkout request failed.');
    return result;
  }

  async function run(label: string, operation: () => Promise<void>) {
    setBusy(label); setMessage(null);
    try { await operation(); } catch (error) { setMessage(error instanceof Error ? error.message : 'The formal checkout request failed.'); }
    finally { setBusy(null); }
  }

  function requestQuote() {
    void run('Binding the formal server price…', async () => {
      const result = await post<CommerceQuote>('quotes', {
        design: preview.snapshot.design, wrist_state: preview.snapshot.wristState, gift_message: preview.snapshot.giftMessage,
        quantity: 1, idempotency_key: keys.current.quote,
      });
      if (result.designer_media && Object.entries(result.designer_media).some(([sku, media]) => (media.version ?? '') !== (INLINE_BY_ID.get(sku)?.mediaVersion ?? ATTACHMENT_BY_ID.get(sku)?.mediaVersion ?? ''))) {
        setQuote(null); setConfirmed(null); throw new Error('Component images changed. Your draft is safe. Refresh this page to review the updated images before quoting again.');
      }
      setQuote(result); setConfirmed(null); setShippingQuote(null); setOrder(null);
      setMessage(result.availability.status === 'sufficient' ? 'Formal quote bound. No inventory is reserved yet.' : 'The exact formal components are not all available.');
    });
  }

  function confirmSnapshot() {
    if (!quote) return;
    void run('Confirming the immutable formal design…', async () => {
      const result = await post<CommerceSnapshot>('snapshots', { quote_id: quote.quote_id, idempotency_key: keys.current.snapshot });
      setConfirmed(result); setShippingQuote(null); setOrder(null); setMessage('Immutable formal snapshot confirmed. Later catalog price changes cannot alter it.');
    });
  }

  function destination(): ShippingDestination {
    return { country: shippingAddress.country, state: shippingAddress.state, postcode: shippingAddress.postcode, city: shippingAddress.city };
  }

  function updateAddress(field: keyof ShippingAddress, value: string) {
    setShippingAddress((current) => {
      if (field !== 'country') return { ...current, [field]: value };
      const states = shippingConfig.states[value] ?? {};
      return { ...current, country: value, state: Object.keys(states)[0] ?? '', postcode: '', city: '' };
    });
    setShippingQuote(null); setShippingRateId(''); setOrder(null);
  }

  function requestShipping() {
    if (!confirmed) return;
    void run('Calculating configured Woo delivery…', async () => {
      const result = await post<ShippingQuote>('shipping', { snapshot_id: confirmed.snapshot_id, shipping_destination: destination() });
      setShippingQuote(result); setShippingRateId(result.selected_rate.rate_id); setMessage(`${result.selected_rate.label}: ${money(result.shipping_minor)} USD.`);
    });
  }

  function checkout() {
    if (!confirmed || !shippingQuote || !shippingRateId) return;
    void run('Creating the unpaid formal Woo order…', async () => {
      const result = await post<CommerceOrder & { payment_available: boolean; order_pay_url: string; payment_message: string }>('checkout', {
        snapshot_id: confirmed.snapshot_id, idempotency_key: keys.current.checkout, shipping_destination: destination(),
        shipping_rate_id: shippingRateId, shipping_address: shippingAddress,
      });
      setOrder(result); setMessage(result.payment_message);
    });
  }

  return <section className="gbm-commerce" aria-labelledby="gbm-formal-preview-title">
    <div className="gbm-commerce__heading">
      <div><p className="gbm-designer-eyebrow">{account.formal_verification ? 'Formal transaction preparation' : 'Order review'}</p><h2 id="gbm-formal-preview-title">Review, reserve, then pay securely</h2></div>
      <strong>{account.formal_verification ? 'STAGING VERIFICATION · PAYMENT BLOCKED' : 'FORMAL CATALOG'}</strong>
    </div>
    <p>{account.formal_verification ? 'The server owns price, catalog mode, inventory, delivery, and the immutable design snapshot. An order stays unpaid until WooCommerce has an approved gateway; the TEST no-charge gateway is never offered here.' : 'Price, exact component availability, delivery, and your confirmed design are checked again before payment. Nothing is charged on this screen.'}</p>
    {!designReady && <p role="status">Confirm the wrist measurement and choose packaging before requesting a formal quote.</p>}
    {busy && <p role="status">{busy}</p>}{message && <p role="status">{message}</p>}
    <div className="gbm-commerce__steps">
      <article><span>1</span><h3>{account.formal_verification ? 'Formal quote' : 'Price & availability'}</h3><button type="button" disabled={!csrf || !designReady || busy !== null} onClick={requestQuote}>{account.formal_verification ? 'Bind server quote' : 'Confirm price'}</button>
        {quote && <small>{account.formal_verification && <>{quote.quote_id}<br /></>}{money(quote.total_minor)} USD · {quote.availability.status}</small>}</article>
      <article><span>2</span><h3>{account.formal_verification ? 'Immutable snapshot' : 'Exact design'}</h3><button type="button" disabled={!quote || quote.fit_guidance?.confirmation_allowed === false || busy !== null} onClick={confirmSnapshot}>Confirm exact design</button>
        {confirmed && <small>{account.formal_verification && <>{confirmed.snapshot_id}<br /></>}Design confirmed</small>}</article>
      <article className="gbm-commerce__delivery"><span>3</span><h3>{account.formal_verification ? 'Delivery & unpaid order' : 'Delivery & order'}</h3>
        <form className="gbm-commerce__checkout-form" onSubmit={(event) => { event.preventDefault(); checkout(); }}>
          <div className="gbm-commerce__address">
            <label>First name<input required value={shippingAddress.first_name} maxLength={60} onChange={(event) => updateAddress('first_name', event.target.value)} /></label>
            <label>Last name<input required value={shippingAddress.last_name} maxLength={60} onChange={(event) => updateAddress('last_name', event.target.value)} /></label>
            <label className="is-wide">Street address<input required value={shippingAddress.address_1} maxLength={100} onChange={(event) => updateAddress('address_1', event.target.value)} /></label>
            <label className="is-wide">Apartment, suite, etc. <span>(optional)</span><input value={shippingAddress.address_2} maxLength={100} onChange={(event) => updateAddress('address_2', event.target.value)} /></label>
            <label>Country or territory<select value={shippingAddress.country} onChange={(event) => updateAddress('country', event.target.value)}>{Object.entries(shippingConfig.countries).map(([code, label]) => <option key={code} value={code}>{label}</option>)}</select></label>
            <label>State or province<select value={shippingAddress.state} onChange={(event) => updateAddress('state', event.target.value)}>{Object.entries(shippingConfig.states[shippingAddress.country] ?? {}).map(([code, label]) => <option key={code} value={code}>{label}</option>)}</select></label>
            <label>City<input required value={shippingAddress.city} maxLength={80} onChange={(event) => updateAddress('city', event.target.value)} /></label>
            <label>ZIP or postal code<input required value={shippingAddress.postcode} maxLength={20} onChange={(event) => updateAddress('postcode', event.target.value)} /></label>
            <label className="is-wide">Contact email<input required type="email" value={shippingAddress.email} maxLength={254} onChange={(event) => updateAddress('email', event.target.value)} /></label>
            <label className="is-wide">Phone <span>(optional)</span><input type="tel" value={shippingAddress.phone} maxLength={40} onChange={(event) => updateAddress('phone', event.target.value)} /></label>
          </div>
          <p className="gbm-commerce__privacy">Address details are stored only in the private order.{account.formal_verification ? ' Staging verification must use synthetic details.' : ''}</p>
          <button type="button" className="is-quiet" disabled={!confirmed || busy !== null} onClick={requestShipping}>Calculate delivery</button>
          {shippingQuote && <div className="gbm-commerce__rates">{shippingQuote.rates.map((rate) => <label key={rate.rate_id}><input type="radio" name="gbm-formal-rate" checked={shippingRateId === rate.rate_id} onChange={() => setShippingRateId(rate.rate_id)} /><span>{rate.label}</span><strong>{money(rate.shipping_minor)} USD</strong></label>)}</div>}
          <button type="submit" disabled={!confirmed || !shippingQuote || !shippingRateId || busy !== null}>{account.formal_verification ? 'Create unpaid Woo order' : 'Review order and continue'}</button>
          {order && <small>Order #{order.order_number} · {money(order.total_minor)} USD · {order.reservation_status}<br />{order.payment_message}</small>}
          {order?.payment_available && <a className="button" href={order.order_pay_url}>Continue to secure payment</a>}
        </form>
      </article>
    </div>
  </section>;
}

function ReviewDesign({ session, onBack, themeStyle, designId, designVersion, ownerScope, onDesignSaved, giftPanel }: {
  giftPanel: import('react').ReactNode; session: ReviewSession; onBack: () => void; themeStyle: CSSProperties; designId: string | null;
  designVersion: number; ownerScope: OwnerScope; onDesignSaved: (id: string, version: number, scope: OwnerScope) => void;
}) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const { preview } = session;
  const formalPreview = CATALOG_CONTEXT.mode === 'formal_preview';
  const formalLive = formalPreview && readAccountContext().formal_commerce_enabled;
  useEffect(() => { headingRef.current?.focus(); }, []);
  return <section className="gbm-designer gbm-review" aria-labelledby="gbm-review-title" style={themeStyle} data-theme={session.themeSlug ?? 'neutral'}>
    <div className="gbm-designer-demo-flag">{PUBLIC_BROWSING ? 'YOUR DESIGN' : formalLive ? 'FORMAL CATALOG · SECURE ORDER PATH' : formalPreview ? 'FORMAL CATALOG · CONTROLLED PREVIEW' : 'STAGING · TEST COMMERCE · NO REAL CHARGE'}</div>
    <header className="gbm-review__header">
      <div>
        <p className="gbm-designer-eyebrow">Your current design · {session.themeName} frame</p>
        <h1 id="gbm-review-title" ref={headingRef} tabIndex={-1}>Review Your Design</h1>
        <p>Check the exact catalog order, quantities, hanging details, and wrist entry before returning to edit. This is not an order or reservation.</p>
      </div>
      <button type="button" className="is-quiet gbm-review-back" onClick={onBack}>Back to editing</button>
    </header>

    <div className="gbm-review__layout">
      <main className="gbm-review__preview" aria-labelledby="gbm-review-preview-title">
        <div className="gbm-designer-panel-heading"><span>01</span><h2 id="gbm-review-preview-title">Arrangement preview</h2></div>
        <BraceletCanvas snapshot={preview.snapshot} />
        <div className="gbm-review__boundary"><strong>{formalLive || PUBLIC_BROWSING ? 'Design illustration' : 'Synthetic demo preview'}</strong><span>{formalLive || PUBLIC_BROWSING ? 'Use this illustration to review arrangement and order; material facts and dimensions remain in the catalog details.' : 'Colors and fixture names are interaction placeholders, not verified stone identities or production bead diameters.'}</span></div>
      </main>

      <aside className="gbm-review__details" aria-label="Design review details">
        <section>
          <div className="gbm-designer-panel-heading"><span>02</span><h2>Actual quantities</h2></div>
          <p className="gbm-review__section-note">{preview.snapshot.design.inline_items.length} inline pieces · {preview.snapshot.design.attachments.length} hanging details</p>
          <ul className="gbm-review__counts">
            {preview.componentSummary.map((item) => {
              const fixture = INLINE_BY_ID.get(item.componentId);
              return <li key={item.componentId}><span><i>{fixture?.imageUrl && <img src={fixture.imageUrl} alt="" width="28" height="28" />}</i>{fixture?.name ?? (formalLive ? 'Catalog component' : 'Unknown synthetic fixture')}{fixture?.nominalMm ? ` · ${fixture.nominalMm} mm` : ''}</span><strong>× {item.count}</strong></li>;
            })}
          </ul>
        </section>

        <section>
          <div className="gbm-designer-panel-heading"><span>03</span><h2>Clockwise order</h2></div>
          <ol className="gbm-review__order">
            {preview.snapshot.design.inline_items.map((item, index) => {
              const fixture = INLINE_BY_ID.get(item.component_id);
              return <li key={item.uid}><span>{index + 1}</span><div><strong>{fixture?.name ?? (formalLive ? 'Catalog component' : 'Unknown synthetic fixture')}</strong><small>{fixture?.nominalMm ? `${fixture.nominalMm} mm · nominal ${formalLive || PUBLIC_BROWSING ? 'catalog' : 'TEST'} size` : 'Size unconfirmed'}</small></div></li>;
            })}
          </ol>
        </section>

        <section>
          <div className="gbm-designer-panel-heading"><span>04</span><h2>Hanging details</h2></div>
          {preview.attachmentAnchors.length === 0 ? <p className="gbm-review__section-note">No hanging details in this design.</p> : <ul className="gbm-review__attachments">
            {preview.attachmentAnchors.map((attachment) => {
              const fixture = ATTACHMENT_BY_ID.get(attachment.componentId);
              const anchor = attachment.anchorPosition === null ? null : preview.snapshot.design.inline_items[attachment.anchorPosition - 1];
              return <li key={attachment.uid}><strong>{fixture?.name ?? (formalLive ? 'Catalog attachment' : 'Unknown synthetic attachment')}</strong><span>{attachment.anchorPosition === null ? 'Anchor unavailable' : `Anchored to piece ${attachment.anchorPosition}: ${INLINE_BY_ID.get(anchor?.component_id ?? '')?.name ?? (formalLive ? 'Catalog component' : 'Unknown fixture')}`}</span></li>;
            })}
          </ul>}
        </section>

        <section className="gbm-review__wrist">
          {preview.snapshot.design.recipient_mode === 'gift' && <p>For someone special · Recipient’s wrist &amp; wear</p>}
          <div className="gbm-designer-panel-heading"><span>05</span><h2>Wrist &amp; wear</h2></div>
          {preview.wrist.status === 'confirmed' && <>
            <strong>Wrist input confirmed</strong>
            <p>{preview.wrist.value} {preview.wrist.unit} · {preferenceLabel(preview.wrist.fitPreference)}</p>
            <small>Stored exactly as entered. This is not a finished inner circumference or fit approval.</small>
          </>}
          {preview.wrist.status === 'pending' && <>
            <strong>Modification pending confirmation</strong>
            <p>{preview.wrist.value.trim() || 'No measurement value entered'}{preview.wrist.value.trim() ? ` ${preview.wrist.unit}` : ''} · {preferenceLabel(preview.wrist.fitPreference)}</p>
            <small>Return to editing to confirm this entry. A previously stored value is not presented here as current approval.</small>
          </>}
          {preview.wrist.status === 'not_provided' && <>
            <strong>Wrist measurement not provided</strong>
            <p>No value or wear preference is being assumed.</p>
            <small>You may return to editing and add one later. Fit remains unknown.</small>
          </>}
          <FitGuidance snapshot={preview.snapshot} confirmed={preview.wrist.status === 'confirmed'} review />
        </section>

        <section className="gbm-review__gift-message">{giftPanel}
          <div className="gbm-designer-panel-heading"><span>06</span><h2>Private gift message</h2></div>
          {preview.snapshot.giftMessage
            ? <p>{preview.snapshot.giftMessage}</p>
            : <p className="gbm-review__section-note">No private gift message.</p>}
          <small>{PUBLIC_BROWSING ? 'Kept with your private design; never placed in a public link.' : formalLive ? 'This stays private and is attached to the order only when you continue through the secure order path.' : formalPreview ? 'This stays in the isolated Formal browser draft unless you deliberately bind a formal quote.' : 'Shown only to this TEST session and authorized workshop staff after confirmation. It is not placed in a URL or public catalog.'}</small>
        </section>
      </aside>
    </div>

    <section className="gbm-review__pending" aria-labelledby="gbm-review-pending-title">
      <div><p className="gbm-designer-eyebrow">{PUBLIC_BROWSING ? 'Your design details' : formalLive ? 'Review before ordering' : 'Not established in this demo'}</p><h2 id="gbm-review-pending-title">{PUBLIC_BROWSING ? 'Details to confirm' : formalLive ? 'Details the server will confirm' : 'Still awaiting real product checks'}</h2></div>
      <dl>
        <div><dt>Real materials &amp; bead diameters</dt><dd>{formalLive ? 'Use the active catalog facts' : 'Confirm material details with us'}</dd></div>
        <div><dt>Finished inner circumference &amp; fit</dt><dd>Not evaluated</dd></div>
        <div><dt>Packaging</dt><dd>{PACKAGING_FIXTURES.find((option) => option.id === preview.snapshot.design.packaging_option_id)?.name ?? 'Not selected'}{preview.snapshot.design.packaging_option_id && !formalPreview && !PUBLIC_BROWSING ? ' · TEST' : ''}</dd></div>
        <div><dt>Price &amp; inventory</dt><dd>Final price and availability to be confirmed</dd></div>
      </dl>
    </section>

    {PUBLIC_BROWSING ? <section className="gbm-commerce"><h2>Keep your design.</h2><p>You can continue editing and use Save &amp; restore to keep this design in your browser or account. <a href="/support/">Contact us</a> to confirm materials, pricing and availability.</p><button onClick={onBack}>Continue designing</button></section> : formalPreview
      ? <FormalCatalogPreviewPanel key={JSON.stringify(preview.snapshot)} preview={preview} />
      : <TestCommercePanel key={JSON.stringify(preview.snapshot)} preview={preview} designId={designId} designVersion={designVersion} ownerScope={ownerScope} onDesignSaved={onDesignSaved} />}

    <footer className="gbm-review__footer">
      {!PUBLIC_BROWSING && <p><strong>{formalLive ? 'Nothing is charged on this review screen.' : 'Staging only.'}</strong> {formalLive ? 'The secure order path rechecks catalog, price, exact inventory, address, and delivery before offering an approved payment method.' : formalPreview ? 'This is a read-only catalog and price preview; cart, checkout, inventory reservation, and ordering remain closed.' : 'Formal confirmation is limited to the controlled no-charge TEST route above; real purchasing remains closed.'}</p>}
      <button type="button" onClick={onBack}>Back to editing</button>
    </footer>
  </section>;
}

function DesignerApp() {
  const [mobile, setMobile] = useState(() => window.matchMedia('(max-width: 767px)').matches);
  useEffect(() => {
    const query = window.matchMedia('(max-width: 767px)');
    const update = () => setMobile(query.matches);
    query.addEventListener('change', update);
    document.body.classList.add('gbm-studio-active');
    return () => { query.removeEventListener('change', update); document.body.classList.remove('gbm-studio-active'); };
  }, []);

  const [initial] = useState(initialState);
  const [writerId] = useState(() => crypto.randomUUID());
  const [history, setHistory] = useState(() => createHistory(initial.snapshot));
  const [storageMode, setStorageMode] = useState(initial.storageMode);
  const [notice, setNotice] = useState<string | null>(initial.notice);
  const [savedAt, setSavedAt] = useState<string | null>(initial.savedAt);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [themeSlug, setThemeSlug] = useState<ThemeSlug | null>(initialTheme);
  const [wristValue, setWristValue] = useState(history.present.design.wrist?.value ?? '');
  const [wristUnit, setWristUnit] = useState<WristUnit>(history.present.design.wrist?.unit ?? 'in');
  const [wristExact, setWristExact] = useState(() => history.present.design.wrist && history.present.wristState ? {
    value: history.present.design.wrist.value,
    unit: history.present.design.wrist.unit,
    normalizedUm: history.present.wristState.normalized_um,
  } : null);
  const [fitPreference, setFitPreference] = useState<FitPreference | null>(history.present.design.fit_preference);
  const [wristMeasurementTouched, setWristMeasurementTouched] = useState(false);
  const [wristError, setWristError] = useState<string | null>(null);
  const [reviewSession, setReviewSession] = useState<ReviewSession | null>(null);
  const [, setLastAdded] = useState<string | null>(null);
  const [commerceDesignId, setCommerceDesignId] = useState<string | null>(null);
  const [commerceDesignVersion, setCommerceDesignVersion] = useState(0);
  const [commerceOwnerScope, setCommerceOwnerScope] = useState<OwnerScope>('session');
  const revisionRef = useRef<number | null>(initial.revision);
  const storageModeRef = useRef<StorageMode>(initial.storageMode);
  const lastSaveInputRef = useRef(history.present);
  const persistenceEpochRef = useRef(0);
  const reviewButtonRef = useRef<HTMLButtonElement>(null);
  const restoreReviewFocusRef = useRef(false);
  const snapshot = history.present;
  const formalPreview = CATALOG_CONTEXT.mode === 'formal_preview';
  const account = useMemo(readAccountContext, []);
  const formalLive = formalPreview && account.formal_commerce_enabled;
  const theme = getDesignerTheme(themeSlug);
  const themeStartingDesign = themeSlug ? THEME_STARTING_DESIGNS.get(themeSlug) : undefined;
  const themeStyle = {
    '--gbm-theme-frame': theme.frame,
    '--gbm-theme-accent': theme.accent,
  } as CSSProperties;

  function setServerIdentity(id: string, version: number, scope: OwnerScope) {
    setCommerceDesignId(id); setCommerceDesignVersion(version); setCommerceOwnerScope(scope);
  }

  function restoreServerSnapshot(restored: Snapshot, scope: OwnerScope) {
    const nextHistory = createHistory(restored);
    lastSaveInputRef.current = nextHistory.present;
    setHistory(nextHistory);
    setPendingDelete(null); setConfirmReset(false); setReviewSession(null); setLastAdded(null);
    setWristValue(restored.design.wrist?.value ?? '');
    setWristUnit(restored.design.wrist?.unit ?? 'in');
    setWristExact(restored.design.wrist && restored.wristState ? { value: restored.design.wrist.value, unit: restored.design.wrist.unit, normalizedUm: restored.wristState.normalized_um } : null);
    setFitPreference(restored.design.fit_preference);
    setWristMeasurementTouched(false); setWristError(null);
    if (scope === 'account') void protectAccountDesignFromBrowserDraft(restored);
  }

  async function protectAccountDesignFromBrowserDraft(protectedSnapshot: Snapshot = history.present) {
    persistenceEpochRef.current += 1;
    lastSaveInputRef.current = protectedSnapshot;
    changeStorageMode('paused');
    if (!initial.lockManager) {
      setNotice('This account design stays only in this tab and your private account. Browser-draft saving is paused.');
      return;
    }
    const locked = await withDraftLock(initial.lockManager, () => clearDraft(
      initial.storage, revisionRef.current, writerId, INLINE_IDS, ATTACHMENT_IDS, initial.storageKey,
    ), initial.lockName);
    if (!locked.ok || !locked.value.ok) {
      pausePersistence('conflict', 'The account design is safe on the server, but another window changed the browser draft. Account-design saving in this tab is paused.');
      return;
    }
    revisionRef.current = locked.value.revision;
    setSavedAt(null);
    setNotice('This account design stays only in this tab and your private account. It was not copied into the shared browser draft.');
  }

  function changeStorageMode(next: StorageMode) {
    storageModeRef.current = next;
    setStorageMode(next);
  }

  function pausePersistence(next: 'conflict' | 'unavailable' | 'paused', message: string) {
    if (storageModeRef.current === 'ready') persistenceEpochRef.current += 1;
    changeStorageMode(next);
    setNotice(message);
    if (next === 'conflict') setReviewSession(null);
  }

  useEffect(() => {
    if (lastSaveInputRef.current === history.present) return;
    lastSaveInputRef.current = history.present;
    if (storageMode !== 'ready') return;
    const persistenceEpoch = persistenceEpochRef.current;
    void (async () => {
      const locked = await withDraftLock(initial.lockManager, () => {
        if (storageModeRef.current !== 'ready' || revisionRef.current === null
          || persistenceEpochRef.current !== persistenceEpoch) return { skipped: true } as const;
        const result = saveDraft(
          initial.storage, history.present, revisionRef.current, writerId, INLINE_IDS, ATTACHMENT_IDS, initial.storageKey,
        );
        if (result.ok) revisionRef.current = result.revision;
        return result;
      }, initial.lockName);
      if (!locked.ok) { pausePersistence('unavailable', locked.message); return; }
      if ('skipped' in locked.value) return;
      const result: DraftWriteResult = locked.value;
      if (result.ok) {
        setSavedAt(result.savedAt);
        return;
      }
      pausePersistence(result.kind === 'conflict' ? 'conflict' : 'unavailable', result.message);
    })();
  }, [history.present, initial.lockManager, initial.storage, storageMode, writerId]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== null && event.key !== initial.storageKey) return;
      if (storageModeRef.current !== 'ready') return;
      const latest = loadDraft(initial.storage, INLINE_IDS, ATTACHMENT_IDS, initial.storageKey);
      if (latest.status === 'unavailable') {
        pausePersistence('unavailable', latest.message);
        return;
      }
      if (latest.status === 'invalid' || latest.revision !== revisionRef.current) {
        pausePersistence('conflict', 'Another window changed the browser draft. This page was kept unchanged and automatic saving is paused.');
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [initial.storage, initial.storageKey]);

  useEffect(() => {
    setWristValue(snapshot.design.wrist?.value ?? '');
    setWristUnit(snapshot.design.wrist?.unit ?? 'in');
    setWristExact(snapshot.design.wrist && snapshot.wristState ? { value: snapshot.design.wrist.value, unit: snapshot.design.wrist.unit, normalizedUm: snapshot.wristState.normalized_um } : null);
    setFitPreference(snapshot.design.fit_preference);
    setWristMeasurementTouched(false);
    setWristError(null);
  }, [snapshot.design.wrist?.value, snapshot.design.wrist?.unit, snapshot.design.fit_preference]);

  useEffect(() => {
    if (!reviewSession && restoreReviewFocusRef.current) {
      restoreReviewFocusRef.current = false;
      reviewButtonRef.current?.focus();
    }
  }, [reviewSession]);

  function apply(result: OperationResult) {
    if (result.error) { setNotice(result.error); return; }
    if (result.snapshot !== snapshot) setHistory((current) => commit(current, result.snapshot));
    if (storageModeRef.current === 'ready') setNotice(null);
  }

  function select(uid: string) {
    setHistory((current) => ({ ...current, present: { ...current.present, selectedInlineUid: uid } }));
  }

  function addPiece(componentId: string) {
    if (INLINE_BY_ID.get(componentId)?.available === false) {
      setNotice('This component is currently unavailable and cannot be added to a new design.');
      return false;
    }
    const result = addInline(snapshot, componentId);
    apply(result);
    if (!result.error && result.snapshot !== snapshot) {
      setLastAdded(INLINE_BY_ID.get(componentId)?.name ?? 'A demo piece');
      return true;
    }
    return false;
  }

  function requestDelete(uid: string) {
    const linked = snapshot.design.attachments.filter((item) => item.anchor_uid === uid).length;
    if (linked > 0) setPendingDelete(uid);
    else apply(removeInline(snapshot, uid));
  }

  function resetLocalState(message: string, nextMode: StorageMode, nextSavedAt: string | null = null) {
    const nextHistory = createHistory(createEmptySnapshot());
    lastSaveInputRef.current = nextHistory.present;
    setHistory(nextHistory);
    setPendingDelete(null);
    setConfirmReset(false);
    setWristValue('');
    setWristUnit('in');
    setWristExact(null);
    setFitPreference(null);
    setWristMeasurementTouched(false);
    setWristError(null);
    setSavedAt(nextSavedAt);
    setReviewSession(null);
    setLastAdded(null);
    changeStorageMode(nextMode);
    setNotice(message);
  }

  async function resetDraft() {
    setConfirmReset(false);
    if (!initial.lockManager) {
      resetLocalState('The design was reset only in this tab. Reliable draft locking is unavailable, so the saved browser draft was not changed.', 'paused');
      return;
    }
    persistenceEpochRef.current += 1;
    const expectedRevision = revisionRef.current;
    const locked = await withDraftLock(initial.lockManager, () => clearDraft(
      initial.storage, expectedRevision, writerId, INLINE_IDS, ATTACHMENT_IDS, initial.storageKey,
    ), initial.lockName);
    if (!locked.ok) { pausePersistence('unavailable', locked.message); return; }
    if (!locked.value.ok) {
      pausePersistence(locked.value.kind === 'conflict' ? 'conflict' : 'unavailable', locked.value.message);
      return;
    }
    revisionRef.current = locked.value.revision;
    resetLocalState('The browser-only draft was reset.', 'ready', locked.value.savedAt);
  }

  async function loadOtherWindowDraft() {
    persistenceEpochRef.current += 1;
    const locked = await withDraftLock(
      initial.lockManager,
      () => loadDraft(initial.storage, INLINE_IDS, ATTACHMENT_IDS, initial.storageKey),
      initial.lockName,
    );
    if (!locked.ok) { pausePersistence('unavailable', locked.message); return; }
    const latest = locked.value;
    if (latest.status === 'invalid' || latest.status === 'unavailable') {
      pausePersistence(latest.status === 'invalid' ? 'conflict' : 'unavailable', latest.message);
      return;
    }
    revisionRef.current = latest.revision;
    const nextHistory = createHistory(latest.snapshot);
    lastSaveInputRef.current = nextHistory.present;
    setHistory(nextHistory);
    setSavedAt(latest.status === 'loaded' ? latest.savedAt : null);
    setReviewSession(null);
    setLastAdded(null);
    changeStorageMode('ready');
    setNotice(latest.status === 'loaded' ? 'The newer browser draft was loaded. Your previous edits in this page were not merged.' : 'The other window cleared the browser draft. A new empty draft was loaded.');
  }

  function keepCurrentPageUnsaved() {
    pausePersistence('paused', 'This page keeps your current edits in memory, but automatic browser saving remains paused. No designs were merged or overwritten.');
  }

  function changeTheme(nextTheme: ThemeSlug | null) {
    setThemeSlug(nextTheme);
    window.history.replaceState(window.history.state, '', replaceThemeInUrl(window.location.href, nextTheme));
  }

  function invalidateWristForEdit() {
    if (snapshot.wristState?.confirmed) {
      setHistory((current) => commit(current, invalidateWristConfirmation(current.present).snapshot));
    }
    setWristError(null);
  }

  function markWristMeasurementDirty() {
    invalidateWristForEdit();
    setWristMeasurementTouched(true);
  }

  function changeWristUnit(nextUnit: WristUnit) {
    if (nextUnit === wristUnit) return;
    if (wristValue.trim() === '') {
      setWristUnit(nextUnit);
      setWristError(null);
      return;
    }
    let exact = wristExact;
    if (!wristMeasurementTouched && snapshot.design.wrist && snapshot.wristState) {
      exact = { value: snapshot.design.wrist.value, unit: snapshot.design.wrist.unit, normalizedUm: snapshot.wristState.normalized_um };
    } else if (!exact) {
      const parsed = parseWristToUm(wristValue, wristUnit);
      if (!parsed.ok) { setWristError(parsed.message); return; }
      exact = { value: parsed.value, unit: parsed.unit, normalizedUm: parsed.normalizedUm };
    }
    setWristExact(exact);
    // Round the converted display only; wristExact remains the source for confirmation.
    setWristValue(nextUnit === 'in' ? (exact.normalizedUm / 25400).toFixed(2) : formatWristUm(exact.normalizedUm, nextUnit));
    setWristUnit(nextUnit);
    setWristError(null);
  }

  function saveWrist() {
    const preserveSavedMeasurement = !wristMeasurementTouched && snapshot.wristState && snapshot.design.wrist;
    const result = preserveSavedMeasurement
      ? confirmWristPreference(snapshot, fitPreference)
      : wristExact
        ? confirmWrist(snapshot, wristExact.value, wristExact.unit, fitPreference)
        : confirmWrist(snapshot, wristValue, wristUnit, fitPreference);
    if (result.error) { setWristError(result.error); return false; }
    apply(result);
    setWristMeasurementTouched(false);
    setWristError(null);
    return true;
  }

  function skipWrist() {
    apply(clearWrist(snapshot));
    setWristValue('');
    setWristUnit('in');
    setWristExact(null);
    setFitPreference(null);
    setWristMeasurementTouched(false);
    setWristError(null);
  }

  function startReview() {
    if (snapshot.design.inline_items.length === 0) {
      setNotice(formalLive ? 'Add at least one available piece before reviewing your design.' : 'Add at least one synthetic demo piece before reviewing your design.');
      return;
    }
    setPendingDelete(null);
    setConfirmReset(false);
    setNotice(null);
    setReviewSession({
      preview: createReviewPreview(snapshot, {
        value: wristValue,
        unit: wristUnit,
        fitPreference,
        hasUnconfirmedChanges: wristMeasurementTouched
          || fitPreference !== snapshot.design.fit_preference
          || Boolean(snapshot.design.wrist && !snapshot.wristState?.confirmed),
      }),
      themeName: theme.name,
      themeSlug,
    });
  }

  function closeReview() {
    restoreReviewFocusRef.current = true;
    setReviewSession(null);
  }

  function setPackaging(value: PackagingOption) {
    apply({ snapshot: { ...snapshot, design: { ...snapshot.design, packaging_option_id: value } } });
  }

  function loadThemeStartingDesign() {
    if (!themeStartingDesign) return;
    const result = applyDemoTemplate(snapshot, {
      ...themeStartingDesign, templateVersion: 'ops-catalog-1', visualCode: 'soft', composition: 'rhythm',
      charmAnchorIndex: themeStartingDesign.attachment?.anchorIndex ?? 0,
    });
    apply(result);
    if (!result.error) setNotice(`${themeStartingDesign.name} loaded from the active ${formalLive ? 'catalog' : formalPreview ? 'formal preview' : PUBLIC_BROWSING ? 'catalog' : 'TEST catalog'}. You can revise every piece.`);
  }

  function loadAcceptanceDesign() {
    const next = createEmptySnapshot();
    const skus = [...Array<string>(16).fill('TEST-AM-6'), ...Array<string>(12).fill('TEST-MS-6'), ...Array<string>(2).fill('TEST-GSP')];
    next.design.inline_items = skus.map((component_id, index) => ({ uid: `piece-${index + 1}`, component_id, orientation_deg: 0 }));
    next.design.attachments = [{ uid: 'charm-1', component_id: 'TEST-MOON', anchor_uid: 'piece-1', orientation_deg: 0 }];
    next.design.wrist = { value: '6.5', unit: 'in' };
    next.design.fit_preference = 'comfort';
    next.design.packaging_option_id = 'standard';
    next.wristState = { normalized_um: 165100, confirmed: true };
    next.selectedInlineUid = 'piece-1';
    next.nextUid = 32;
    setHistory((current) => commit(current, next));
    setNotice('The documented acceptance arrangement was loaded with confirmed TEST inputs. Current pricing still requires a server quote.');
  }

  const pendingLinks = pendingDelete ? snapshot.design.attachments.filter((item) => item.anchor_uid === pendingDelete).length : 0;
  const wristConfirmed = Boolean(snapshot.wristState?.confirmed && !wristMeasurementTouched);

  const giftPanel = (<>
            <label htmlFor="gbm-packaging">{PUBLIC_BROWSING ? 'Packaging' : formalPreview ? 'Catalog packaging' : 'TEST packaging'}</label>
            <select id="gbm-packaging" value={snapshot.design.packaging_option_id ?? ''} onChange={(event) => event.target.value && setPackaging(event.target.value as PackagingOption)}>
              <option value="">Choose packaging</option>
              {PACKAGING_FIXTURES.map((option) => <option key={option.id} value={option.id}>{option.name} · {money(option.priceMinor)}</option>)}
            </select>
            <label htmlFor="gbm-gift-message">Private gift message (optional)</label>
            <textarea id="gbm-gift-message" rows={4} maxLength={MAX_GIFT_MESSAGE} value={snapshot.giftMessage}
              onChange={(event) => apply({ snapshot: { ...snapshot, giftMessage: event.target.value } })}
              aria-describedby="gbm-gift-message-help" />
            <p id="gbm-gift-message-help" className="gbm-designer-wrist__help">Plain text, up to {MAX_GIFT_MESSAGE} characters. It is kept out of URLs, catalog data, analytics, and default Woo order fields.</p>
            <small>{[...snapshot.giftMessage].length} / {MAX_GIFT_MESSAGE}</small>
  </>);
  const wristPanel = (onConfirmed: () => void) => (
          <section className="gbm-designer-wrist" aria-labelledby="gbm-wrist-title">
            <div>
              <p className="gbm-designer-wrist__eyebrow">Optional measurement</p>
              <h3 id="gbm-wrist-title">Wrist &amp; wear</h3>
              <p>Measure your wrist close to the skin. This is not the bracelet's finished inner circumference.</p>
            </div>
            <div className="gbm-designer-unit-switch" role="group" aria-label="Wrist measurement unit">
              <button type="button" aria-pressed={wristUnit === 'in'} onClick={() => changeWristUnit('in')}>Inches</button>
              <button type="button" aria-pressed={wristUnit === 'cm'} onClick={() => changeWristUnit('cm')}>Centimeters</button>
            </div>
            <label htmlFor="gbm-wrist-value">Wrist circumference ({wristUnit})</label>
            <input id="gbm-wrist-value" type="text" inputMode="decimal" autoComplete="off" placeholder={wristUnit === 'in' ? '6.5' : '16.51'}
              value={wristValue} aria-describedby="gbm-wrist-help" aria-invalid={wristError ? 'true' : undefined}
              onChange={(event) => {
                markWristMeasurementDirty();
                setWristValue(event.target.value);
                const parsed = parseWristToUm(event.target.value, wristUnit);
                setWristExact(parsed.ok ? { value: parsed.value, unit: parsed.unit, normalizedUm: parsed.normalizedUm } : null);
              }} />
            <p id="gbm-wrist-help" className="gbm-designer-wrist__help">Use up to three decimal places. This entry does not confirm bracelet fit.</p>
            <label htmlFor="gbm-fit-preference">Fit preference</label>
            <select id="gbm-fit-preference" value={fitPreference ?? ''} onChange={(event) => {
              invalidateWristForEdit();
              setFitPreference(event.target.value === '' ? null : event.target.value as FitPreference);
            }}>
              <option value="">Choose one</option>
              <option value="snug">Close</option>
              <option value="comfort">Comfortable</option>
              <option value="loose">Loose</option>
            </select>
            <details><summary>How to measure</summary><p>Wrap a flexible tape around your wrist where you will wear the bracelet. Keep it against the skin without squeezing. Enter that measurement without adding extra length; choose your preferred fit separately.</p></details>
            {wristError && <p className="gbm-designer-wrist__error" role="alert">{wristError}</p>}
            <div className="gbm-designer-wrist__actions">
              <button type="button" onClick={() => { if (saveWrist()) onConfirmed(); }}>Confirm size</button>
              <button type="button" className="is-quiet" onClick={skipWrist}>Skip for now</button>
            </div>
            <div className={`gbm-designer-wrist__status ${wristConfirmed ? 'is-confirmed' : ''}`} role="status">
              <strong>{wristConfirmed ? 'Wrist measurement confirmed' : snapshot.design.wrist ? 'Changes need confirmation' : 'Optional for now'}</strong>
              {wristConfirmed && <StartingGuide snapshot={snapshot} />}
              <span>No fit claim or bead count change is made here.</span>
            </div>
          </section>
  );

  if (reviewSession) {
    return <ReviewDesign giftPanel={giftPanel} session={{ ...reviewSession, preview: createReviewPreview(snapshot, { value: wristValue, unit: wristUnit, fitPreference, hasUnconfirmedChanges: wristMeasurementTouched || fitPreference !== snapshot.design.fit_preference || Boolean(snapshot.design.wrist && !snapshot.wristState?.confirmed) }) }} onBack={closeReview} themeStyle={themeStyle}
      designId={commerceDesignId} designVersion={commerceDesignVersion} ownerScope={commerceOwnerScope} onDesignSaved={setServerIdentity} />;
  }

  return <MobileDesigner desktop={!mobile} wristConfirmed={wristConfirmed} reviewButtonRef={reviewButtonRef} snapshot={snapshot}
    canvas={<BraceletCanvas snapshot={snapshot} onSelect={select} />}
    saved={storageMode === 'ready' ? formatTime(savedAt) : 'Saving paused'} notice={notice}
    onAdd={addPiece} onReplace={(uid, id) => {
      if (!snapshot.design.inline_items.some(item => item.uid === uid)) return false;
      const result = replaceSelected({ ...snapshot, selectedInlineUid: uid }, id);
      apply(result); return !result.error;
    }}
    onSelect={select}
    onMove={(direction) => snapshot.selectedInlineUid && apply(moveInline(snapshot, snapshot.selectedInlineUid, direction))}
    onRemove={() => snapshot.selectedInlineUid && requestDelete(snapshot.selectedInlineUid)}
    onUndo={() => setHistory(current => undo(current))} onRedo={() => setHistory(current => redo(current))}
    canUndo={history.past.length > 0} canRedo={history.future.length > 0} onReview={startReview}
    onCharm={(id) => snapshot.selectedInlineUid && apply(addAttachment(snapshot, id, snapshot.selectedInlineUid))}
    onRemoveCharm={(uid) => apply(removeAttachment(snapshot, uid))}
    onMoveCharm={(uid,anchor) => apply(moveAttachmentAnchor(snapshot, uid, anchor))}
    wristPanel={wristPanel} meaningPanel={<div><h2>Choose your intention</h2><p>Symbolic meaning is personal, not a promise of health, wealth or safety.</p>{THEME_CHOICES.map(choice => <button key={choice.slug ?? 'neutral'} aria-pressed={themeSlug === choice.slug} onClick={() => changeTheme(choice.slug)}>{choice.name}</button>)}</div>}
    tools={<>{!formalPreview && <ServerDesignControls snapshot={snapshot} designId={commerceDesignId} designVersion={commerceDesignVersion} ownerScope={commerceOwnerScope} onIdentity={setServerIdentity} onRestore={restoreServerSnapshot} onAccountProtected={() => void protectAccountDesignFromBrowserDraft()} />}
      {!formalPreview && <QuizRecovery snapshot={snapshot} enabled={storageMode === 'ready'} />}
      {!formalPreview && readAccountContext().can_manage_studio && <button onClick={loadAcceptanceDesign}>Load documented TEST design</button>}
      {themeStartingDesign && <button onClick={loadThemeStartingDesign}>Load starting design</button>}
      {storageMode === 'conflict' && <><button onClick={() => void loadOtherWindowDraft()}>Load newer saved draft</button><button onClick={keepCurrentPageUnsaved}>Keep this page unsaved</button></>}
      <button onClick={() => setConfirmReset(true)}>Reset design</button>
      {confirmReset && <div role="alert"><p>Reset this browser-only draft?</p><button onClick={() => void resetDraft()}>Yes, reset it</button><button onClick={() => setConfirmReset(false)}>Keep my draft</button></div>}
    </>}
    confirmation={pendingDelete && <div role="alert"><p>Remove this piece and its {pendingLinks} linked attachment(s)?</p><button onClick={() => { apply(removeInline(snapshot, pendingDelete)); setPendingDelete(null); }}>Remove together</button><button onClick={() => setPendingDelete(null)}>Cancel</button></div>}
  />;

}

const mount = document.getElementById('gbm-studio-demo');
const quizMount = document.getElementById('gbm-quiz-demo');
const catalogSource = mount ?? quizMount;
if (catalogSource?.dataset.gbmCatalog) {
  try { configureCatalog(JSON.parse(catalogSource.dataset.gbmCatalog)); } catch { /* Keep reviewed fallback fixtures. */ }
}
if (quizMount?.dataset.gbmRecommendation) {
  try { configureFormalRecommendation(JSON.parse(quizMount.dataset.gbmRecommendation)); } catch { /* Formal QuizApp renders a no-fallback state. */ }
}
if (mount) createRoot(mount).render(<StrictMode><DesignerApp /></StrictMode>);
if (quizMount) createRoot(quizMount).render(<StrictMode><QuizApp /></StrictMode>);
